import { DownOutlined, IdcardOutlined, UserOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  message,
  Switch,
  Tooltip,
} from 'antd';
import { Select } from 'components';
import { EventsApiService } from 'features/events/api/events-api.service';
import { handleKeyAmount, handleKeyDownThree } from 'features/events/utils';
import { FormSectionsSeparator } from 'features/users/components/modals/form-sections-separator';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AgeGenderItem, PriceCategoryFormField } from './types';

export const AddPriceCategoryForm = ({
  editId,
  form,
  selectedSeats,
  setSelectedSeats,
  layoutType,
  filteredSeatArray,
  getEventDetaildata,
  isFamily,
  setIsFamily,
  familyParticipants,
  setFamilyParticipants,
}: {
  editId: any;
  form: any;
  getEventDetaildata: any;
  selectedSeats: number[];
  setSelectedSeats: Dispatch<SetStateAction<number[]>>;
  layoutType: string;
  filteredSeatArray: any;
  isFamily: boolean;
  setIsFamily: Dispatch<SetStateAction<boolean>>;
  familyParticipants: AgeGenderItem[];
  setFamilyParticipants: Dispatch<SetStateAction<AgeGenderItem[]>>;
}) => {
  const { t } = useTranslation();
  const category = localStorage.getItem('categoryName');
  const Gender = [
    { value: 1, label: 'Male' },
    { value: 2, label: 'Female' },
    { value: 3, label: 'Both' },
  ];

  const [registrationType, setRegistrationType] = useState({
    items: [],
  });

  const getRegistrationType = async () => {
    try {
      const apires = await EventsApiService().sportCategoryId(
        `Flag=${'RegistrationType'}` as any,
      );
      setRegistrationType(apires as any);
    } catch (error) {
      message.error('');
    }
  };

  const [registrationTypeValue, setRegistrationTypeValue] = useState<number>(
    editId?.RegistrationType,
  );

  const isRegistrationDone = Boolean(
    editId?.isRegistiondone ||
      editId?.isRegistionDone ||
      editId?.IsRegistiondone ||
      editId?.IsRegistionDone,
  );

  const minPartDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const maxPartDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const indMaxPartDebounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    getRegistrationType();

    return () => {
      if (minPartDebounceRef.current) clearTimeout(minPartDebounceRef.current);
      if (maxPartDebounceRef.current) clearTimeout(maxPartDebounceRef.current);
      if (indMaxPartDebounceRef.current)
        clearTimeout(indMaxPartDebounceRef.current);
    };
  }, []);

  const handleRegistrationTypeChange = (value: number) => {
    setRegistrationTypeValue(value);
    if (value === 3) {
      form.setFieldsValue({
        [PriceCategoryFormField.MinPI]: 1,
      });
    }
  };
  const isNormalLayout = layoutType === 'Normal';
  const isEventTicketing = getEventDetaildata?.events_ticketing;
  useEffect(() => {
    const shouldForceRegistrationType =
      layoutType === 'Layout' || (isNormalLayout && isEventTicketing);

    setRegistrationTypeValue(
      shouldForceRegistrationType ? 1 : editId?.RegistrationType,
    );
  }, [editId, layoutType, getEventDetaildata]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow only numbers (0-9) and backspace
    if (!(event.key === 'Backspace' || /[0-9]/.test(event.key))) {
      event.preventDefault();
    }
  };
  const validateMaxAmount = (_: any, value: number) => {
    if (!value) {
      return Promise.resolve();
    }
    const stringValue = String(value);
    const regex = /^\d{0,4}(\.\d{0,3})?$/;

    if (regex.test(stringValue)) {
      return Promise.resolve(); // Valid input
    } else {
      return Promise.reject(
        new Error(
          'Please enter a valid numeric value with at most four digits and up to three decimal places',
        ),
      );
    }
  };

  const validateAgeFrom = (_: any, value: string) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      const num = parseInt(value, 10);
      if (num <= 0) {
        return Promise.reject(new Error('Age From must be greater than 0'));
      }
    }

    return Promise.resolve();
  };

  const validateAgeTo = (_: any, value: string) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      const num = parseInt(value, 10);
      if (num <= 0) {
        return Promise.reject(new Error('Age To must be greater than 0'));
      }
    }
    const ageFrom = form.getFieldValue(PriceCategoryFormField.AgeFrom);
    if (value && ageFrom && parseInt(value, 10) <= parseInt(ageFrom, 10)) {
      return Promise.reject(new Error('Age To must be greater than Age From'));
    }

    return Promise.resolve();
  };

  const validateTeamMaxPart = (_: any, value: string) => {
    const minPart = form.getFieldValue(PriceCategoryFormField.MinPT);
    if (value && minPart && parseInt(value, 10) < parseInt(minPart, 10)) {
      return Promise.reject(
        new Error(
          'Maximum number should be greater than or equal to minimum value',
        ),
      );
    }

    return Promise.resolve();
  };

  const validateIndividualMaxPart = (_: any, value: string) => {
    const minPart = form.getFieldValue(PriceCategoryFormField.MinPI);
    if (value && minPart && parseInt(value, 10) < parseInt(minPart, 10)) {
      return Promise.reject(
        new Error(
          'Maximum number should be greater than or equal to minimum value',
        ),
      );
    }

    return Promise.resolve();
  };
  const handleCheckboxChange = (seatNumber: number): void => {
    setSelectedSeats(
      (prev) =>
        prev.includes(seatNumber)
          ? prev.filter((seat) => seat !== seatNumber) // remove
          : [...prev, seatNumber], // add
    );
  };

  const participantPoolRef = useRef<Map<number, AgeGenderItem>>(new Map());

  useEffect(() => {
    const rawList = editId?.ageGenderList || editId?.AgeGenderList;
    let parsedList = rawList;
    if (typeof rawList === 'string') {
      try {
        parsedList = JSON.parse(rawList);
      } catch {
        parsedList = [];
      }
    }
    if (Array.isArray(parsedList) && parsedList.length > 0) {
      participantPoolRef.current.clear();
      parsedList.forEach((item: any, idx: number) => {
        participantPoolRef.current.set(idx, {
          id: item.id ?? item.Id ? Number(item.id ?? item.Id) : null,
          text: item.text || item.Text || `Participant ${idx + 1}`,
          text_Ar:
            item.text_Ar || item.Text_Ar || item.text_ar || item.Text_ar || '',
          orderNo: idx + 1,
          gender:
            item.gender ?? item.Gender
              ? Number(item.gender ?? item.Gender)
              : undefined,
          ageFrom: item.ageFrom ?? item.AgeFrom ?? null,
          ageTo: item.ageTo ?? item.AgeTo ?? null,
          wAgeFrom: item.wAgeFrom ?? item.WAgeFrom ?? item.wageFrom ?? null,
          wAgeTo: item.wAgeTo ?? item.WAgeTo ?? item.wageTo ?? null,
          isMandatory: Boolean(item.isMandatory ?? item.IsMandatory ?? false),
        });
      });
    }
  }, [editId]);

  useEffect(() => {
    if (familyParticipants && familyParticipants.length > 0) {
      familyParticipants.forEach((p, idx) => {
        participantPoolRef.current.set(idx, { ...p, orderNo: idx + 1 });
      });
    }
  }, [familyParticipants]);

  const getParticipantForIndex = (index: number): AgeGenderItem => {
    const cached = participantPoolRef.current.get(index);
    if (cached && (isParticipantFilled(cached) || cached.id)) {
      return {
        ...cached,
        orderNo: index + 1,
      };
    }

    return {
      id: null,
      text: `Participant ${index + 1}`,
      text_Ar: '',
      orderNo: index + 1,
      gender: undefined,
      ageFrom: null,
      ageTo: null,
      wAgeFrom: null,
      wAgeTo: null,
      isMandatory: false,
    };
  };

  const handleToggleFamily = (checked: boolean) => {
    if (isRegistrationDone) return;
    setIsFamily(checked);
    if (checked && familyParticipants.length === 0) {
      const minCount =
        parseInt(form.getFieldValue(PriceCategoryFormField.MinPT) || '1', 10) ||
        1;
      const maxCount = parseInt(
        form.getFieldValue(PriceCategoryFormField.MaxPT) || '0',
        10,
      );
      let initialCount = Math.max(minCount, 1);
      if (maxCount > 0 && initialCount > maxCount) {
        initialCount = maxCount;
      }
      const initialList: AgeGenderItem[] = Array.from(
        { length: initialCount },
        (_, i) => ({
          id: null,
          text: `Participant ${i + 1}`,
          text_Ar: '',
          orderNo: i + 1,
          gender: undefined,
          ageFrom: null,
          ageTo: null,
          wAgeFrom: null,
          wAgeTo: null,
          isMandatory: false,
        }),
      );
      setFamilyParticipants(initialList);
    }
  };

  const isParticipantFilled = (p: AgeGenderItem) => {
    return (
      Boolean(p.id) ||
      Boolean(p.isMandatory) ||
      (p.gender !== undefined && p.gender !== null) ||
      (p.ageFrom !== null &&
        p.ageFrom !== undefined &&
        String(p.ageFrom).trim() !== '') ||
      (p.ageTo !== null &&
        p.ageTo !== undefined &&
        String(p.ageTo).trim() !== '') ||
      (p.wAgeFrom !== null &&
        p.wAgeFrom !== undefined &&
        String(p.wAgeFrom).trim() !== '') ||
      (p.wAgeTo !== null &&
        p.wAgeTo !== undefined &&
        String(p.wAgeTo).trim() !== '') ||
      Boolean(p.text_Ar && p.text_Ar.trim() !== '') ||
      Boolean(
        p.text &&
          p.text.trim() !== '' &&
          p.text.trim() !== `Participant ${p.orderNo}`,
      )
    );
  };

  const syncParticipantsOnMinChange = (minVal: number, maxVal: number) => {
    if (!isFamily) return;

    if (maxVal > 0 && minVal > 0 && minVal > maxVal) {
      return;
    }

    setFamilyParticipants((prev) => {
      prev.forEach((p, idx) => {
        participantPoolRef.current.set(idx, { ...p, orderNo: idx + 1 });
      });

      let next = [...prev];

      // 1. Strict Max Limit: Always truncate to maxVal if maxVal > 0
      if (maxVal > 0 && next.length > maxVal) {
        next = next.slice(0, maxVal);
      }

      const targetMin = maxVal > 0 && maxVal < minVal ? maxVal : minVal;

      // 2. Expand if below targetMin
      if (targetMin > 0 && next.length < targetMin) {
        const added: AgeGenderItem[] = Array.from(
          { length: targetMin - next.length },
          (_, i) => getParticipantForIndex(next.length + i),
        );
        next = [...next, ...added];
      }

      // 3. Lowering Min Part: Remove unedited trailing cards down to targetMin
      if (targetMin > 0 && next.length > targetMin) {
        while (next.length > targetMin) {
          const lastItem = next[next.length - 1];
          if (!isParticipantFilled(lastItem)) {
            next.pop();
          } else {
            break;
          }
        }
      }

      // 4. Ensure mandatory count does not exceed targetMin
      if (targetMin > 0) {
        let mCount = 0;
        next = next.map((p) => {
          if (p.isMandatory) {
            mCount++;
            if (mCount > targetMin) {
              return { ...p, isMandatory: false };
            }
          }

          return p;
        });
      }

      return next.map((p, idx) => ({ ...p, orderNo: idx + 1 }));
    });
  };

  const syncParticipantsOnMaxChange = (minVal: number, maxVal: number) => {
    if (!isFamily) return;

    // When user types a maxVal that is less than minVal (e.g. typing '1' for '10'),
    // do not truncate participants. It is an invalid intermediate input state.
    if (maxVal > 0 && minVal > 0 && maxVal < minVal) {
      return;
    }

    setFamilyParticipants((prev) => {
      prev.forEach((p, idx) => {
        participantPoolRef.current.set(idx, { ...p, orderNo: idx + 1 });
      });

      let next = [...prev];

      // 1. Strict Max Limit: Always truncate to maxVal if maxVal > 0
      if (maxVal > 0 && next.length > maxVal) {
        next = next.slice(0, maxVal);
      }

      // 2. Expand if below min requirement (clamped to maxVal)
      const targetMin = maxVal > 0 && maxVal < minVal ? maxVal : minVal;
      if (targetMin > 0 && next.length < targetMin) {
        const added: AgeGenderItem[] = Array.from(
          { length: targetMin - next.length },
          (_, i) => getParticipantForIndex(next.length + i),
        );
        next = [...next, ...added];
      }

      return next.map((p, idx) => ({ ...p, orderNo: idx + 1 }));
    });
  };

  const handleMinPartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10) || 0;
    if (minPartDebounceRef.current) {
      clearTimeout(minPartDebounceRef.current);
    }
    minPartDebounceRef.current = setTimeout(() => {
      const maxPart = parseInt(
        form.getFieldValue(PriceCategoryFormField.MaxPT) || '0',
        10,
      );
      syncParticipantsOnMinChange(val, maxPart);
      if (form.getFieldValue(PriceCategoryFormField.MaxPT)) {
        form
          .validateFields([PriceCategoryFormField.MaxPT])
          .catch(() => undefined);
      }
    }, 300);
  };

  const handleMinPartBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (minPartDebounceRef.current) {
      clearTimeout(minPartDebounceRef.current);
    }
    const val = parseInt(e.target.value, 10) || 0;
    const maxPart = parseInt(
      form.getFieldValue(PriceCategoryFormField.MaxPT) || '0',
      10,
    );
    syncParticipantsOnMinChange(val, maxPart);
    if (form.getFieldValue(PriceCategoryFormField.MaxPT)) {
      form
        .validateFields([PriceCategoryFormField.MaxPT])
        .catch(() => undefined);
    }
  };

  const handleMaxPartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const maxVal = parseInt(e.target.value, 10) || 0;
    if (maxPartDebounceRef.current) {
      clearTimeout(maxPartDebounceRef.current);
    }
    maxPartDebounceRef.current = setTimeout(() => {
      const minPart = parseInt(
        form.getFieldValue(PriceCategoryFormField.MinPT) || '0',
        10,
      );
      syncParticipantsOnMaxChange(minPart, maxVal);
      form
        .validateFields([PriceCategoryFormField.MaxPT])
        .catch(() => undefined);
    }, 300);
  };

  const handleMaxPartBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (maxPartDebounceRef.current) {
      clearTimeout(maxPartDebounceRef.current);
    }
    const maxVal = parseInt(e.target.value, 10) || 0;
    const minPart = parseInt(
      form.getFieldValue(PriceCategoryFormField.MinPT) || '0',
      10,
    );
    syncParticipantsOnMaxChange(minPart, maxVal);
    form.validateFields([PriceCategoryFormField.MaxPT]).catch(() => undefined);
  };

  const handleIndividualMaxPartChange = () => {
    if (indMaxPartDebounceRef.current) {
      clearTimeout(indMaxPartDebounceRef.current);
    }
    indMaxPartDebounceRef.current = setTimeout(() => {
      form
        .validateFields([PriceCategoryFormField.MaxPI])
        .catch(() => undefined);
    }, 300);
  };

  const handleIndividualMaxPartBlur = () => {
    if (indMaxPartDebounceRef.current) {
      clearTimeout(indMaxPartDebounceRef.current);
    }
    form.validateFields([PriceCategoryFormField.MaxPI]).catch(() => undefined);
  };

  const updateParticipantText = (orderNo: number, text: string) => {
    if (text.length <= 15) {
      setFamilyParticipants((prev) =>
        prev.map((p) => (p.orderNo === orderNo ? { ...p, text } : p)),
      );
    }
  };

  const updateParticipantTextAr = (orderNo: number, text_Ar: string) => {
    if (text_Ar.length <= 15) {
      setFamilyParticipants((prev) =>
        prev.map((p) => (p.orderNo === orderNo ? { ...p, text_Ar } : p)),
      );
    }
  };

  const updateParticipantField = (
    orderNo: number,
    field: keyof AgeGenderItem,
    value: any,
  ) => {
    setFamilyParticipants((prev) =>
      prev.map((p) => (p.orderNo === orderNo ? { ...p, [field]: value } : p)),
    );
  };

  const watchedMinPT = Form.useWatch(PriceCategoryFormField.MinPT, form);
  const watchedMaxPT = Form.useWatch(PriceCategoryFormField.MaxPT, form);
  const watchedRegType = Form.useWatch(
    PriceCategoryFormField.RegistrationType,
    form,
  );

  const effectiveRegType =
    registrationTypeValue ??
    Number(
      watchedRegType ||
        form.getFieldValue(PriceCategoryFormField.RegistrationType),
    );

  const parsedMinPT =
    parseInt(
      watchedMinPT !== undefined && watchedMinPT !== ''
        ? watchedMinPT
        : form.getFieldValue(PriceCategoryFormField.MinPT) || '0',
      10,
    ) || 0;

  const parsedMaxPT =
    parseInt(
      watchedMaxPT !== undefined && watchedMaxPT !== ''
        ? watchedMaxPT
        : form.getFieldValue(PriceCategoryFormField.MaxPT) || '0',
      10,
    ) || 0;

  const isTeamFamily = effectiveRegType === 2 && Boolean(isFamily);

  // In Team registration with per-participant rules (isFamily):
  // Show common age and gender ONLY when max part is strictly greater than min part (maxPart > minPart).
  // When min and max are equal (minPart === maxPart), do NOT show common age and gender.
  const showCommonAgeAndGender = isTeamFamily
    ? parsedMinPT > 0 && parsedMaxPT > parsedMinPT
    : true;

  useEffect(() => {
    if (!showCommonAgeAndGender) {
      form.setFieldsValue({
        [PriceCategoryFormField.GenderType]: undefined,
        [PriceCategoryFormField.AgeFrom]: undefined,
        [PriceCategoryFormField.AgeTo]: undefined,
      });
    }
  }, [showCommonAgeAndGender, form]);

  const minParticipants = Math.max(parsedMinPT || 1, 1);
  const mandatoryCount = familyParticipants.filter((p) => p.isMandatory).length;

  const handleMandatoryChange = (orderNo: number, checked: boolean) => {
    if (checked && mandatoryCount >= minParticipants) {
      message.warning(
        `You can mark at most ${minParticipants} participant(s) as mandatory based on Min Part.`,
      );

      return;
    }
    updateParticipantField(orderNo, 'isMandatory', checked);
  };

  const handleGenderChange = (orderNo: number, val: number) => {
    setFamilyParticipants((prev) =>
      prev.map((p) => {
        if (p.orderNo !== orderNo) return p;

        return {
          ...p,
          gender: val,
          ageFrom: val === 2 ? null : p.ageFrom,
          ageTo: val === 2 ? null : p.ageTo,
          wAgeFrom: val === 1 ? null : p.wAgeFrom,
          wAgeTo: val === 1 ? null : p.wAgeTo,
        };
      }),
    );
  };

  const deleteParticipant = (orderNo: number) => {
    setFamilyParticipants((prev) =>
      prev
        .filter((p) => p.orderNo !== orderNo)
        .map((p, idx) => ({ ...p, orderNo: idx + 1 })),
    );
  };

  const addParticipant = () => {
    const maxPart = parseInt(
      form.getFieldValue(PriceCategoryFormField.MaxPT) || '0',
      10,
    );
    if (maxPart > 0 && familyParticipants.length >= maxPart) {
      message.error(
        `Cannot add more participants. Maximum limit (${maxPart}) reached.`,
      );

      return;
    }

    const newParticipant = getParticipantForIndex(familyParticipants.length);
    setFamilyParticipants((prev) => [...prev, newParticipant]);
  };

  const isDisabled =
    layoutType === 'Layout' ||
    (layoutType === 'Normal' && Boolean(getEventDetaildata?.events_ticketing));

  const genderLabel = isFamily
    ? registrationTypeValue === 3
      ? 'Gender'
      : registrationTypeValue === 2
      ? 'Common Gender'
      : 'Gender'
    : 'Gender';
  const ageFromLabel = isFamily
    ? registrationTypeValue === 3
      ? 'Age From'
      : registrationTypeValue === 2
      ? 'Common Age From'
      : 'Age From'
    : 'Age From';
  const ageToLabel = isFamily
    ? registrationTypeValue === 3
      ? 'Age To'
      : registrationTypeValue === 2
      ? 'Common Age To'
      : 'Age To'
    : 'Age To';

  return (
    <FormSectionsSeparator title={''}>
      <>
        {editId && (
          <Form.Item
            label={'Price Type'}
            // name={PriceCategoryFormField.PriceType}
          >
            <Input
              prefix={<UserOutlined className='input-prefix-icon' />}
              value={
                editId?.price_type_name ? editId?.price_type_name : 'Normal'
              }
              disabled
            />
          </Form.Item>
        )}
      </>
      <Form.Item<PriceCategoryFormField>
        label={'Category Title'}
        name={PriceCategoryFormField.Category}
        rules={[
          {
            required: true,
            whitespace: true,
            message: t('validateMessages.custom.nomsg'),
          },
        ]}
      >
        <Input
          placeholder={'Enter Category Title'}
          prefix={<UserOutlined className='input-prefix-icon' />}
        />
      </Form.Item>
      <Form.Item<PriceCategoryFormField>
        label={'Category Arabic'}
        name={PriceCategoryFormField.CategoryAr}
        rules={[
          {
            required: true,
            whitespace: true,
            message: t('validateMessages.custom.nomsg'),
          },
        ]}
      >
        <Input
          placeholder={'Enter Category Arabic'}
          prefix={<UserOutlined className='input-prefix-icon' />}
        />
      </Form.Item>

      <Form.Item<PriceCategoryFormField>
        label={'Description'}
        name={PriceCategoryFormField.Description}
        // rules={[
        //     {
        //         required: true,
        //         whitespace: true,
        //     },
        // ]}
      >
        <Input
          placeholder={'Enter Description'}
          prefix={<UserOutlined className='input-prefix-icon' />}
        />
      </Form.Item>

      {editId?.price_type_name === 'Early Bird' && (
        <>
          <Form.Item<PriceCategoryFormField>
            label={'Date From'}
            name={PriceCategoryFormField.DateFrom}
            rules={[
              {
                required: true,
                message: t('validateMessages.custom.nomsg'),
              },
            ]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item<PriceCategoryFormField>
            label={'Date To'}
            name={PriceCategoryFormField.DateTo}
            rules={[
              {
                required: true,
                message: t('validateMessages.custom.nomsg'),
              },
            ]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </>
      )}

      <Form.Item<PriceCategoryFormField>
        label={'No. of Persons'}
        name={PriceCategoryFormField.NoOfPersons}
        rules={[
          {
            required: true,
            message: t('validateMessages.custom.nomsg'),
          },
        ]}
      >
        <Input
          placeholder={'Enter No. of Persons'}
          pattern='[0-9]*'
          onKeyDown={handleKeyDown}
        />
      </Form.Item>

      <Form.Item<PriceCategoryFormField>
        label={'Registration'}
        name={PriceCategoryFormField.RegistrationType}
        rules={[
          {
            required: true,
            message: t('validateMessages.custom.nomsg'),
          },
        ]}
      >
        <Select
          options={registrationType?.items.map((res: any) => ({
            label: res.Text,
            value: res.Id,
          }))}
          placeholder={'Select Registration'}
          disabled={isDisabled}
          className='has-prefix-icon'
          onChange={handleRegistrationTypeChange}
          suffixIcon={
            <>
              <IdcardOutlined className='prefix-icon' />
              <DownOutlined className='suffix-icon' />
            </>
          }
        />
      </Form.Item>

      {(registrationTypeValue === 2 || registrationTypeValue === 3) && (
        <div
          style={{
            margin: '14px 0',
            padding: '12px 14px',
            background: '#fafafa',
            borderRadius: '8px',
            border: '1px solid #f0f0f0',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <div
                style={{ fontWeight: 600, fontSize: '14px', color: '#1f1f1f' }}
              >
                Is family?
              </div>
              <div
                style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '2px' }}
              >
                Set per-participant age rules instead of one common rule.
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: isFamily ? '#52c41a' : '#8c8c8c',
                }}
              >
                {isFamily ? 'Yes' : 'No'}
              </span>
              <Tooltip
                title={
                  isRegistrationDone
                    ? "Registration has already been done for this ticket. 'Is family' cannot be changed."
                    : undefined
                }
              >
                <span>
                  <Switch
                    disabled={isRegistrationDone}
                    checked={isFamily}
                    onChange={handleToggleFamily}
                    style={{
                      backgroundColor: isFamily ? '#52c41a' : undefined,
                    }}
                  />
                </span>
              </Tooltip>
            </div>
          </div>

          {isFamily && (
            <div
              style={{ fontSize: '12px', color: '#595959', marginTop: '10px' }}
            >
              Team size: <strong>{familyParticipants.length}</strong> · each
              participant is validated by their own role and age.
            </div>
          )}
        </div>
      )}

      <div className='add_tct'>
        {(registrationTypeValue === 1 || registrationTypeValue === 3) && (
          <div className='add_tct_wrap'>
            <div className='add_tct_col'>
              <Form.Item
                label='Individual Price'
                name={PriceCategoryFormField.PriceIn}
                style={{ width: '100%' }}
                rules={[
                  {
                    required: true,
                    message: t('validateMessages.custom.nomsg'),
                  },
                  {
                    validator: validateMaxAmount,
                  },
                ]}
              >
                <Input
                  placeholder='Individual Price'
                  onKeyDown={handleKeyAmount}
                />
              </Form.Item>
            </div>
            {category !== 'Layout' && (
              <>
                <div className='add_tct_col'>
                  <Form.Item
                    label='Individual Min Part'
                    name={PriceCategoryFormField.MinPI}
                  >
                    <Input placeholder='Individual Min Part' readOnly />
                  </Form.Item>
                </div>

                <div className='add_tct_col'>
                  <Form.Item
                    label='Individual Max Part'
                    name={PriceCategoryFormField.MaxPI}
                    dependencies={[PriceCategoryFormField.MinPI]}
                    validateTrigger={['onBlur']}
                    rules={[
                      {
                        required: true,
                        message: t('validateMessages.custom.nomsg'),
                      },
                      {
                        validator: validateIndividualMaxPart,
                      },
                    ]}
                  >
                    <Input
                      placeholder='Individual Max Part'
                      onKeyDown={handleKeyDownThree}
                      onChange={handleIndividualMaxPartChange}
                      onBlur={handleIndividualMaxPartBlur}
                    />
                  </Form.Item>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className='add_tct'>
        {(registrationTypeValue === 2 || registrationTypeValue === 3) && (
          <div className='add_tct_wrap'>
            <div className='add_tct_col'>
              <Form.Item
                label='Team Amount'
                name={PriceCategoryFormField.PriceTe}
                rules={[
                  {
                    required: true,
                    message: t('validateMessages.custom.nomsg'),
                  },
                  {
                    validator: validateMaxAmount,
                  },
                ]}
              >
                <Input
                  placeholder={'Team Amount'}
                  onKeyDown={handleKeyAmount}
                />
              </Form.Item>
            </div>
            <div className='add_tct_col'>
              <Form.Item
                label='Team Min Part'
                name={PriceCategoryFormField.MinPT}
                validateTrigger={['onBlur']}
                rules={[
                  {
                    required: true,
                    message: t('validateMessages.custom.nomsg'),
                  },
                ]}
              >
                <Input
                  placeholder={'Team Min Part'}
                  onKeyDown={handleKeyDownThree}
                  onChange={handleMinPartChange}
                  onBlur={handleMinPartBlur}
                />
              </Form.Item>
            </div>
            <div className='add_tct_col'>
              <Form.Item
                label='Team Max Part'
                name={PriceCategoryFormField.MaxPT}
                dependencies={[PriceCategoryFormField.MinPT]}
                validateTrigger={['onBlur']}
                rules={[
                  {
                    required: true,
                    message: t('validateMessages.custom.nomsg'),
                  },
                  {
                    validator: validateTeamMaxPart,
                  },
                ]}
              >
                <Input
                  placeholder={'Team Max Part'}
                  onKeyDown={handleKeyDownThree}
                  onChange={handleMaxPartChange}
                  onBlur={handleMaxPartBlur}
                />
              </Form.Item>
            </div>
          </div>
        )}
      </div>

      {showCommonAgeAndGender && (
        <div className='add_tct'>
          <div className='add_tct_wrap'>
            <div className='add_tct_col' style={{ width: '33.33%' }}>
              <Form.Item<PriceCategoryFormField>
                label={genderLabel}
                name={PriceCategoryFormField.GenderType}
                rules={[
                  {
                    required: showCommonAgeAndGender,
                    message: t('validateMessages.custom.nomsg'),
                  },
                ]}
              >
                <Select
                  options={Gender}
                  placeholder={`Select ${genderLabel}`}
                  className='has-prefix-icon'
                  suffixIcon={
                    <>
                      <IdcardOutlined className='prefix-icon' />
                      <DownOutlined className='suffix-icon' />
                    </>
                  }
                />
              </Form.Item>
            </div>

            <div className='add_tct_col' style={{ width: '33.33%' }}>
              <Form.Item
                label={ageFromLabel}
                name={PriceCategoryFormField.AgeFrom}
                validateTrigger={['onChange', 'onBlur']}
                rules={[
                  {
                    required: showCommonAgeAndGender,
                    message: t('validateMessages.custom.nomsg'),
                  },
                  { validator: validateAgeFrom },
                ]}
              >
                <Input
                  placeholder={`Enter ${ageFromLabel}`}
                  prefix={<UserOutlined className='input-prefix-icon' />}
                  onKeyDown={handleKeyDownThree}
                />
              </Form.Item>
            </div>

            <div className='add_tct_col' style={{ width: '33.33%' }}>
              <Form.Item
                label={ageToLabel}
                name={PriceCategoryFormField.AgeTo}
                dependencies={[PriceCategoryFormField.AgeFrom]}
                validateTrigger={['onChange', 'onBlur']}
                rules={[
                  {
                    required: showCommonAgeAndGender,
                    message: t('validateMessages.custom.nomsg'),
                  },
                  { validator: validateAgeTo },
                ]}
              >
                <Input
                  placeholder={`Enter ${ageToLabel}`}
                  prefix={<UserOutlined className='input-prefix-icon' />}
                  onKeyDown={handleKeyDownThree}
                />
              </Form.Item>
            </div>
          </div>
        </div>
      )}

      {(registrationTypeValue === 2 || registrationTypeValue === 3) &&
        isFamily && (
          <div style={{ marginBottom: '16px' }}>
            {familyParticipants.map((participant, index) => (
              <div
                key={participant.orderNo}
                style={{
                  background: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  padding: '14px',
                  marginBottom: '12px',
                }}
              >
                {/* Header: editable badges for English & Arabic and remove button */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        background: '#e6f4ff',
                        border: '1px solid #91caff',
                        borderRadius: '16px',
                        padding: '2px 10px',
                      }}
                    >
                      <input
                        type='text'
                        maxLength={15}
                        value={participant.text}
                        onChange={(e) =>
                          updateParticipantText(
                            participant.orderNo,
                            e.target.value,
                          )
                        }
                        placeholder={`Participant ${index + 1}`}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: '#0958d9',
                          fontWeight: 600,
                          fontSize: '13px',
                          outline: 'none',
                          width: `${
                            Math.max(participant.text?.length || 11, 11) * 8 +
                            12
                          }px`,
                          maxWidth: '140px',
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        background: '#e6f4ff',
                        border: '1px solid #91caff',
                        borderRadius: '16px',
                        padding: '2px 10px',
                      }}
                    >
                      <input
                        type='text'
                        maxLength={15}
                        value={participant.text_Ar || ''}
                        onChange={(e) =>
                          updateParticipantTextAr(
                            participant.orderNo,
                            e.target.value,
                          )
                        }
                        placeholder='الاسم بالعربية'
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: '#0958d9',
                          fontWeight: 600,
                          fontSize: '13px',
                          outline: 'none',
                          width: `${
                            Math.max(
                              (participant.text_Ar || '')?.length || 10,
                              10,
                            ) *
                              8 +
                            12
                          }px`,
                          maxWidth: '140px',
                        }}
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <Checkbox
                      checked={participant.isMandatory || false}
                      disabled={
                        !participant.isMandatory &&
                        mandatoryCount >= minParticipants
                      }
                      onChange={(e) =>
                        handleMandatoryChange(
                          participant.orderNo,
                          e.target.checked,
                        )
                      }
                    >
                      <span
                        style={{
                          fontSize: '13px',
                          fontWeight: 500,
                          color:
                            !participant.isMandatory &&
                            mandatoryCount >= minParticipants
                              ? '#bfbfbf'
                              : '#434343',
                          cursor:
                            !participant.isMandatory &&
                            mandatoryCount >= minParticipants
                              ? 'not-allowed'
                              : 'pointer',
                        }}
                      >
                        Is Mandatory
                      </span>
                    </Checkbox>
                    <button
                      type='button'
                      onClick={() => deleteParticipant(participant.orderNo)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#8c8c8c',
                        fontSize: '18px',
                        lineHeight: 1,
                        padding: '0 4px',
                      }}
                      title='Remove participant'
                    >
                      &times;
                    </button>
                  </div>
                </div>

                {/* Gender select */}
                <div style={{ marginBottom: '12px' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      color: '#666',
                      marginBottom: '4px',
                    }}
                  >
                    Gender
                  </label>
                  <Select
                    value={participant.gender || undefined}
                    onChange={(val) =>
                      handleGenderChange(participant.orderNo, val)
                    }
                    options={Gender}
                    style={{ width: '100%' }}
                    placeholder='Select Gender'
                  />
                </div>

                {/* Age inputs */}
                {participant.gender === 3 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                    }}
                  >
                    {(() => {
                      const isMaleAgeError =
                        (participant.ageFrom !== null &&
                          participant.ageFrom !== undefined &&
                          participant.ageTo !== null &&
                          participant.ageTo !== undefined &&
                          Number(participant.ageTo) <
                            Number(participant.ageFrom)) ||
                        (participant.ageFrom !== null &&
                          participant.ageFrom !== undefined &&
                          Number(participant.ageFrom) <= 0) ||
                        (participant.ageTo !== null &&
                          participant.ageTo !== undefined &&
                          Number(participant.ageTo) <= 0);
                      const maleErrorMessage =
                        (participant.ageFrom !== null &&
                          Number(participant.ageFrom) <= 0) ||
                        (participant.ageTo !== null &&
                          Number(participant.ageTo) <= 0)
                          ? 'Age must be greater than 0'
                          : 'Male Age To must be greater than or equal to Age From';
                      const isFemaleAgeError =
                        (participant.wAgeFrom !== null &&
                          participant.wAgeFrom !== undefined &&
                          participant.wAgeTo !== null &&
                          participant.wAgeTo !== undefined &&
                          Number(participant.wAgeTo) <
                            Number(participant.wAgeFrom)) ||
                        (participant.wAgeFrom !== null &&
                          participant.wAgeFrom !== undefined &&
                          Number(participant.wAgeFrom) <= 0) ||
                        (participant.wAgeTo !== null &&
                          participant.wAgeTo !== undefined &&
                          Number(participant.wAgeTo) <= 0);
                      const femaleErrorMessage =
                        (participant.wAgeFrom !== null &&
                          Number(participant.wAgeFrom) <= 0) ||
                        (participant.wAgeTo !== null &&
                          Number(participant.wAgeTo) <= 0)
                          ? 'Age must be greater than 0'
                          : 'Age To must be greater than or equal to Age From';

                      return (
                        <>
                          <div>
                            <div
                              style={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#555',
                                marginBottom: '4px',
                              }}
                            >
                              Male Age Rule
                            </div>
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '10px',
                              }}
                            >
                              <div>
                                <label
                                  style={{
                                    display: 'block',
                                    fontSize: '12px',
                                    color: '#666',
                                    marginBottom: '4px',
                                  }}
                                >
                                  Age from
                                </label>
                                <Input
                                  placeholder='Age from'
                                  status={isMaleAgeError ? 'error' : undefined}
                                  value={participant.ageFrom ?? ''}
                                  onKeyDown={handleKeyDownThree}
                                  onChange={(e) =>
                                    updateParticipantField(
                                      participant.orderNo,
                                      'ageFrom',
                                      e.target.value !== ''
                                        ? Number(e.target.value)
                                        : null,
                                    )
                                  }
                                />
                              </div>
                              <div>
                                <label
                                  style={{
                                    display: 'block',
                                    fontSize: '12px',
                                    color: '#666',
                                    marginBottom: '4px',
                                  }}
                                >
                                  Age to
                                </label>
                                <Input
                                  placeholder='Age to'
                                  status={isMaleAgeError ? 'error' : undefined}
                                  value={participant.ageTo ?? ''}
                                  onKeyDown={handleKeyDownThree}
                                  onChange={(e) =>
                                    updateParticipantField(
                                      participant.orderNo,
                                      'ageTo',
                                      e.target.value !== ''
                                        ? Number(e.target.value)
                                        : null,
                                    )
                                  }
                                />
                              </div>
                            </div>
                            {isMaleAgeError && (
                              <div
                                style={{
                                  color: '#ff4d4f',
                                  fontSize: '12px',
                                  marginTop: '4px',
                                }}
                              >
                                {maleErrorMessage}
                              </div>
                            )}
                          </div>
                          <div>
                            <div
                              style={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#555',
                                marginBottom: '4px',
                              }}
                            >
                              Female Age Rule
                            </div>
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '10px',
                              }}
                            >
                              <div>
                                <label
                                  style={{
                                    display: 'block',
                                    fontSize: '12px',
                                    color: '#666',
                                    marginBottom: '4px',
                                  }}
                                >
                                  Age from
                                </label>
                                <Input
                                  placeholder='Age from'
                                  status={
                                    isFemaleAgeError ? 'error' : undefined
                                  }
                                  value={participant.wAgeFrom ?? ''}
                                  onKeyDown={handleKeyDownThree}
                                  onChange={(e) =>
                                    updateParticipantField(
                                      participant.orderNo,
                                      'wAgeFrom',
                                      e.target.value !== ''
                                        ? Number(e.target.value)
                                        : null,
                                    )
                                  }
                                />
                              </div>
                              <div>
                                <label
                                  style={{
                                    display: 'block',
                                    fontSize: '12px',
                                    color: '#666',
                                    marginBottom: '4px',
                                  }}
                                >
                                  Age to
                                </label>
                                <Input
                                  placeholder='Age to'
                                  status={
                                    isFemaleAgeError ? 'error' : undefined
                                  }
                                  value={participant.wAgeTo ?? ''}
                                  onKeyDown={handleKeyDownThree}
                                  onChange={(e) =>
                                    updateParticipantField(
                                      participant.orderNo,
                                      'wAgeTo',
                                      e.target.value !== ''
                                        ? Number(e.target.value)
                                        : null,
                                    )
                                  }
                                />
                              </div>
                            </div>
                            {isFemaleAgeError && (
                              <div
                                style={{
                                  color: '#ff4d4f',
                                  fontSize: '12px',
                                  marginTop: '4px',
                                }}
                              >
                                {femaleErrorMessage}
                              </div>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                ) : participant.gender === 2 ? (
                  <div>
                    {(() => {
                      const isFemaleAgeError =
                        (participant.wAgeFrom !== null &&
                          participant.wAgeFrom !== undefined &&
                          participant.wAgeTo !== null &&
                          participant.wAgeTo !== undefined &&
                          Number(participant.wAgeTo) <
                            Number(participant.wAgeFrom)) ||
                        (participant.wAgeFrom !== null &&
                          participant.wAgeFrom !== undefined &&
                          Number(participant.wAgeFrom) <= 0) ||
                        (participant.wAgeTo !== null &&
                          participant.wAgeTo !== undefined &&
                          Number(participant.wAgeTo) <= 0);
                      const femaleErrorMessage =
                        (participant.wAgeFrom !== null &&
                          Number(participant.wAgeFrom) <= 0) ||
                        (participant.wAgeTo !== null &&
                          Number(participant.wAgeTo) <= 0)
                          ? 'Age must be greater than 0'
                          : 'Age To must be greater than or equal to Age From';

                      return (
                        <>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '10px',
                            }}
                          >
                            <div>
                              <label
                                style={{
                                  display: 'block',
                                  fontSize: '12px',
                                  color: '#666',
                                  marginBottom: '4px',
                                }}
                              >
                                Age from
                              </label>
                              <Input
                                placeholder='Age from'
                                status={isFemaleAgeError ? 'error' : undefined}
                                value={participant.wAgeFrom ?? ''}
                                onKeyDown={handleKeyDownThree}
                                onChange={(e) =>
                                  updateParticipantField(
                                    participant.orderNo,
                                    'wAgeFrom',
                                    e.target.value !== ''
                                      ? Number(e.target.value)
                                      : null,
                                  )
                                }
                              />
                            </div>
                            <div>
                              <label
                                style={{
                                  display: 'block',
                                  fontSize: '12px',
                                  color: '#666',
                                  marginBottom: '4px',
                                }}
                              >
                                Age to
                              </label>
                              <Input
                                placeholder='Age to'
                                status={isFemaleAgeError ? 'error' : undefined}
                                value={participant.wAgeTo ?? ''}
                                onKeyDown={handleKeyDownThree}
                                onChange={(e) =>
                                  updateParticipantField(
                                    participant.orderNo,
                                    'wAgeTo',
                                    e.target.value !== ''
                                      ? Number(e.target.value)
                                      : null,
                                  )
                                }
                              />
                            </div>
                          </div>
                          {isFemaleAgeError && (
                            <div
                              style={{
                                color: '#ff4d4f',
                                fontSize: '12px',
                                marginTop: '4px',
                              }}
                            >
                              {femaleErrorMessage}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div>
                    {(() => {
                      const isAgeError =
                        (participant.ageFrom !== null &&
                          participant.ageFrom !== undefined &&
                          participant.ageTo !== null &&
                          participant.ageTo !== undefined &&
                          Number(participant.ageTo) <
                            Number(participant.ageFrom)) ||
                        (participant.ageFrom !== null &&
                          participant.ageFrom !== undefined &&
                          Number(participant.ageFrom) <= 0) ||
                        (participant.ageTo !== null &&
                          participant.ageTo !== undefined &&
                          Number(participant.ageTo) <= 0);
                      const ageErrorMessage =
                        (participant.ageFrom !== null &&
                          Number(participant.ageFrom) <= 0) ||
                        (participant.ageTo !== null &&
                          Number(participant.ageTo) <= 0)
                          ? 'Age must be greater than 0'
                          : 'Age To must be greater than or equal to Age From';

                      return (
                        <>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '10px',
                            }}
                          >
                            <div>
                              <label
                                style={{
                                  display: 'block',
                                  fontSize: '12px',
                                  color: '#666',
                                  marginBottom: '4px',
                                }}
                              >
                                Age from
                              </label>
                              <Input
                                placeholder='Age from'
                                status={isAgeError ? 'error' : undefined}
                                value={participant.ageFrom ?? ''}
                                onKeyDown={handleKeyDownThree}
                                onChange={(e) =>
                                  updateParticipantField(
                                    participant.orderNo,
                                    'ageFrom',
                                    e.target.value !== ''
                                      ? Number(e.target.value)
                                      : null,
                                  )
                                }
                              />
                            </div>
                            <div>
                              <label
                                style={{
                                  display: 'block',
                                  fontSize: '12px',
                                  color: '#666',
                                  marginBottom: '4px',
                                }}
                              >
                                Age to
                              </label>
                              <Input
                                placeholder='Age to'
                                status={isAgeError ? 'error' : undefined}
                                value={participant.ageTo ?? ''}
                                onKeyDown={handleKeyDownThree}
                                onChange={(e) =>
                                  updateParticipantField(
                                    participant.orderNo,
                                    'ageTo',
                                    e.target.value !== ''
                                      ? Number(e.target.value)
                                      : null,
                                  )
                                }
                              />
                            </div>
                          </div>
                          {isAgeError && (
                            <div
                              style={{
                                color: '#ff4d4f',
                                fontSize: '12px',
                                marginTop: '4px',
                              }}
                            >
                              {ageErrorMessage}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            ))}

            <Button
              type='dashed'
              onClick={addParticipant}
              disabled={(() => {
                const maxPart = parseInt(
                  form.getFieldValue(PriceCategoryFormField.MaxPT) || '0',
                  10,
                );

                return maxPart > 0 && familyParticipants.length >= maxPart;
              })()}
              style={{ width: '100%', marginBottom: '12px' }}
            >
              + Add Participant
            </Button>
          </div>
        )}

      <Form.Item<PriceCategoryFormField>
        label={'CategoryId'}
        name={PriceCategoryFormField.CategoryId}
        rules={[
          {
            required: true,
            message: t('validateMessages.custom.nomsg'),
          },
        ]}
      >
        <Input placeholder={'Enter CategoryId'} onKeyDown={handleKeyDown} />
      </Form.Item>
      {layoutType === 'Layout' && (
        <Form.Item<PriceCategoryFormField>
          label={'Color Code'}
          name={PriceCategoryFormField.ColorCode}
          rules={[
            {
              required: true,
              message: t('validateMessages.custom.nomsg'),
              whitespace: true,
            },
          ]}
        >
          <Input placeholder={'Enter Color Code'} />
        </Form.Item>
      )}

      {layoutType === 'Layout' && (
        <Form.Item<PriceCategoryFormField> label={'Available Seat'}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              marginBottom: 20,
            }}
          >
            {filteredSeatArray && filteredSeatArray.length > 0 ? (
              filteredSeatArray.map((seat: number) => (
                <Checkbox
                  key={seat}
                  value={seat}
                  onChange={() => handleCheckboxChange(seat)}
                  checked={selectedSeats?.includes(seat)}
                >
                  {seat}
                </Checkbox>
              ))
            ) : (
              <span style={{ color: '#ff4d4f', fontSize: '13px' }}>
                No seat available
              </span>
            )}
          </div>
        </Form.Item>
      )}

      <Form.Item<PriceCategoryFormField>
        // label={"Is Refundable"}
        name={PriceCategoryFormField.IsRefundable}
        valuePropName='checked'
      >
        <Checkbox>Is Refundable</Checkbox>
      </Form.Item>

      <Form.Item noStyle dependencies={[PriceCategoryFormField.IsRefundable]}>
        {({ getFieldValue }) => {
          const isRefundable = getFieldValue([
            PriceCategoryFormField.IsRefundable,
          ]);

          return isRefundable ? (
            <>
              <Form.Item
                name={PriceCategoryFormField.Refund_price}
                label={'Refund Amount'}
                rules={[
                  {
                    required: true,
                    message: t('validateMessages.custom.nomsg'),
                  },
                  {
                    validator(_, value) {
                      if (!value) {
                        return Promise.resolve();
                      }
                      const stringValue = String(value);
                      const parsedValue = stringValue.match(/^\d*\.?\d{0,3}$/);
                      if (parsedValue) {
                        return Promise.resolve();
                      }

                      return Promise.reject(
                        new Error(
                          'Please enter a valid numeric value with at most three decimal places',
                        ),
                      );
                    },
                  },
                ]}
              >
                <Input
                  placeholder='Refund Amount'
                  onKeyDown={handleKeyAmount}
                />
              </Form.Item>
              <Form.Item
                name={PriceCategoryFormField.Refund_desc}
                label={'Refund Description'}
                rules={[
                  {
                    required: true,
                    message: t('validateMessages.custom.nomsg'),
                  },
                ]}
              >
                <Input placeholder='Refund Description ' />
              </Form.Item>
            </>
          ) : null;
        }}
      </Form.Item>
      {layoutType === 'Normal' && (
        <Form.Item<PriceCategoryFormField>
          // label={"CategoryId"}
          name={PriceCategoryFormField.IsDelivarable}
          valuePropName='checked'
        >
          <Checkbox>Is Delivery</Checkbox>
        </Form.Item>
      )}
      <Form.Item noStyle dependencies={[PriceCategoryFormField.IsDelivarable]}>
        {({ getFieldValue }) => {
          const IsDelivared = getFieldValue([
            PriceCategoryFormField.IsDelivarable,
          ]);

          return IsDelivared ? (
            <>
              <Form.Item
                name={PriceCategoryFormField.Delivery_price}
                label={'Delivery Price'}
                rules={[
                  {
                    required: true,
                    message: t('validateMessages.custom.nomsg'),
                  },
                  {
                    validator: validateMaxAmount,
                  },
                ]}
              >
                <Input
                  placeholder='Delivery Price'
                  onKeyDown={handleKeyAmount}
                />
              </Form.Item>
              <Form.Item
                name={PriceCategoryFormField.Delivery_desc}
                label={'Delivery Description'}
                rules={[
                  {
                    required: true,
                    message: t('validateMessages.custom.nomsg'),
                  },
                ]}
              >
                <Input placeholder='Delivery  Description ' />
              </Form.Item>
            </>
          ) : null;
        }}
      </Form.Item>

      {layoutType !== 'Layout' && (
        <>
          <Form.Item<PriceCategoryFormField>
            // label={"CategoryId"}
            name={PriceCategoryFormField.IsWaiting}
            valuePropName='checked'
          >
            <Checkbox>Is Waiting</Checkbox>
          </Form.Item>
          <Form.Item noStyle dependencies={[PriceCategoryFormField.IsWaiting]}>
            {({ getFieldValue }) => {
              const IsWait = getFieldValue([PriceCategoryFormField.IsWaiting]);

              return IsWait ? (
                <>
                  <Form.Item
                    name={PriceCategoryFormField.TotalWaiting}
                    label={'Total Waiting'}
                    rules={[
                      {
                        required: true,
                        message: t('validateMessages.custom.nomsg'),
                      },
                      {
                        validator: validateMaxAmount,
                      },
                    ]}
                  >
                    <Input
                      placeholder='Total Waiting'
                      onKeyDown={handleKeyAmount}
                    />
                  </Form.Item>

                  <Form.Item
                    label={'Waiting Start Date'}
                    name={PriceCategoryFormField.RegistStartDate}
                    rules={[
                      {
                        required: true,
                        message: t('validateMessages.custom.nomsg'),
                      },
                      // validateDates(form.getFieldValue),
                    ]}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item
                    label={'Waiting End Date'}
                    name={PriceCategoryFormField.RegistEndDate}
                    rules={[
                      {
                        required: true,
                        message: t('validateMessages.custom.nomsg'),
                      },
                      // validateDates(form.getFieldValue),
                    ]}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </>
              ) : null;
            }}
          </Form.Item>

          <Form.Item<PriceCategoryFormField>
            // label={"CategoryId"}
            name={PriceCategoryFormField.IsHiddenFromGroup}
            valuePropName='checked'
          >
            <Checkbox>Hide From Group Registration</Checkbox>
          </Form.Item>
        </>
      )}

      {/* <Form.Item<PriceCategoryFormField>
                // label={"CategoryId"}
                name={PriceCategoryFormField.ShowTicketLayout}
                valuePropName="checked"

            >
                <Checkbox>Show Ticket Layout</Checkbox>
            </Form.Item> */}
    </FormSectionsSeparator>
  );
};
